import * as React from 'react';
import { format, isBefore, startOfDay } from 'date-fns';
import type { Locale } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { getDateLocale } from '../../../lib/date-locale';
import { cn } from '../../../lib/utils';
import { Calendar } from '../../ui/calendar';
import { formatTimeOfDay, TimeOfDaySelect } from '../time-of-day-select';
import type { TimeOfDay } from '../time-of-day-select';

export type { TimeOfDay };

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DateTimeRangeValue {
  date: Date;
  end: TimeOfDay;
  start: TimeOfDay;
}

export interface DateTimeRangePickerProps {
  className?: string;
  onChange: (value: DateTimeRangeValue) => void;
  /** Label shown above the live summary line (the pop-in titles differ — e.g. "Requested timewindow" vs "Timeslot"). */
  summaryLabel: string;
  /** Separator text between the start and end time selects (e.g. "to"). */
  toLabel: string;
  value: DateTimeRangeValue;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isPastDay(date: Date): boolean {
  return isBefore(startOfDay(date), startOfDay(new Date()));
}

function formatSummary(value: DateTimeRangeValue, locale: Locale): string {
  const datePart = format(value.date, 'EEE d MMM', { locale });
  const timePart = `${formatTimeOfDay(value.start)}–${formatTimeOfDay(value.end)}`;
  return `${datePart} · ${timePart}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Single-day date + time-range picker: Monday-first calendar (past days disabled) on the left,
 * hour/minute selects for the start and end of the window plus a live summary line on the right.
 * V1 scope is single-day / single-slot only — no multi-day, recurring, or multi-slot modes.
 */
export function DateTimeRangePicker({
  className,
  onChange,
  summaryLabel,
  toLabel,
  value,
}: DateTimeRangePickerProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const locale = getDateLocale(i18n.language);

  function handleDateSelect(date: Date | undefined): void {
    if (date) onChange({ ...value, date });
  }

  return (
    <div className={cn('flex flex-col sm:flex-row', className)}>
      <div className="flex justify-center bg-muted/30 sm:bg-transparent">
        <Calendar
          disabled={isPastDay}
          mode="single"
          onSelect={handleDateSelect}
          selected={value.date}
          weekStartsOn={1}
        />
      </div>
      <div className="flex w-full min-w-0 flex-col gap-4 border-t bg-background p-4 sm:w-[280px] sm:border-l sm:border-t-0">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <TimeOfDaySelect onChange={(start): void => onChange({ ...value, start })} time={value.start} />
            <span className="text-xs text-muted-foreground">{toLabel}</span>
            <TimeOfDaySelect onChange={(end): void => onChange({ ...value, end })} time={value.end} />
          </div>
        </div>
        <div className="space-y-1 border-t pt-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{summaryLabel}</p>
          <p className="text-sm font-semibold leading-snug text-foreground">{formatSummary(value, locale)}</p>
        </div>
      </div>
    </div>
  );
}
